import React, { useRef, useMemo } from 'react';
import { theme } from '../../../theme';
import type { TimeBin } from './KalenderwocheBarChart';
import type { PeriodResidueBin, PrimaryAxisVolumeBin } from '../../../services/api';
import { useEChartsLifecycle, INVISIBLE_TOOLTIP_OPTS } from './_chartCommon';
import { useCategoryMeta } from '../../../context/ClassificationSchemaContext';
import { volumeStackKeys, classifiedBinTotals, residueByBinLabel, barLabelText, RESIDUE_LABEL_RICH, withResidueBins } from './_volumeStack';

// <purpose>KW.Balken — gradient bars + count labels, no tooltip popup. Hover-morph:
// hovering a bar dispatches highlight to the solid + K-stack series → solid fades,
// K-stack reveals. Invisible tooltip keeps axisPointer.shadow without a popup box.</purpose>
// <constraints>
//   All bar series silent:true — native per-K emphasis disabled; only the axisPointer-
//   driven dispatch loop controls the morph reveal.
//   First K-series uses barGap:'-100%' to overlay the K-stack on top of the solid bar.
//   One stacked series per cm.categoryKeys entry EXCEPT the unclassified value (gMSB: K0–K4; VNB: marktprozess)
//   — part 7 round 2, 2026-10-13: the unclassified value is the lack of a category and is in no series; the bar
//   total is the classified volume (classifiedBinTotals), so the stack still sums to it. The residue (failed +
//   without a category) is the label's second small line „+f +w" (part 7 round 5, 2026-10-14; see _volumeStack).
// </constraints>

type Props = {
  data: TimeBin[];
  rawData?: PrimaryAxisVolumeBin[];
  filterHash?: string;
  /** stats.perPeriodResidue — absent ⇒ no „+n" line on any bar. */
  residue?: PeriodResidueBin[];
};

export const KwBalken: React.FC<Props> = ({ data: servedData, rawData, filterHash, residue }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // The axis: classified bins UNION residue bins (a residue-only period stands at 0 with its „+n").
  const data = useMemo(() => withResidueBins(servedData, residue), [servedData, residue]);
  const cm = useCategoryMeta();
  // part 7 round 2, 2026-10-13: the unclassified value is no series and no part of the bar total (see _volumeStack).
  const stackKeys = useMemo(() => volumeStackKeys(cm), [cm]);

  const kPerBin = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    if (rawData?.length) {
      for (const b of rawData) {
        if (!map.has(b.binLabel)) map.set(b.binLabel, {});
        const entry = map.get(b.binLabel)!;
        entry[b.primaryAxis] = (entry[b.primaryAxis] ?? 0) + b.count;
      }
    }
    return map;
  }, [rawData]);
  const residueMap = useMemo(() => residueByBinLabel(residue), [residue]);

  useEChartsLifecycle(containerRef, chart => {
    const NAVY = theme.colors.navy;
    const NAVY_DARK = theme.colors.navyDark;
    const NAVY_MID = theme.colors.navyMid;

    const labels = data.map(d => d.label);
    const values = classifiedBinTotals(data, rawData, cm);

    const kSeriesData = stackKeys.reduce<Record<string, number[]>>((acc, k) => {
      acc[k] = data.map(d => kPerBin.get(d.label)?.[k] ?? 0);
      return acc;
    }, {});

    chart.setOption({
      grid: { left: 36, right: 16, top: 38, bottom: 26 },
      tooltip: INVISIBLE_TOOLTIP_OPTS,
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: '#dde0e8' } },
        axisTick: { show: false },
        axisLabel: { color: '#9095a3', fontSize: 9, interval: data.length > 16 ? 1 : 0 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#f1f2f6' } },
        axisLabel: { color: '#9095a3', fontSize: 9 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          name: 'solid',
          type: 'bar',
          silent: true,
          data: values,
          barWidth: '60%',
          itemStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: NAVY_DARK },
                { offset: 1, color: NAVY_MID },
              ],
            },
            borderRadius: [4, 4, 0, 0],
            shadowColor: 'rgba(6,16,90,0.16)',
            shadowBlur: 4,
            shadowOffsetY: 2,
          },
          emphasis: { itemStyle: { opacity: 0 } },
          label: {
            show: true,
            position: 'top',
            color: NAVY,
            fontSize: 10,
            fontWeight: 600,
            formatter: (p: any) => barLabelText(values[p.dataIndex] ?? 0, residueMap?.get(labels[p.dataIndex])),
            rich: RESIDUE_LABEL_RICH,
          },
          z: 1,
        },
        ...stackKeys.map((k, i) => ({
          name: k,
          type: 'bar' as const,
          silent: true,
          stack: 'k',
          ...(i === 0 ? { barGap: '-100%' } : {}),
          barWidth: '60%',
          data: kSeriesData[k],
          itemStyle: {
            color: cm.color(k),
            opacity: 0,
          },
          label: { show: false },
          emphasis: { itemStyle: { opacity: 0.92 } },
          z: 2,
        })),
      ],
    });

    // Hover-morph: axisPointer dispatch loop drives highlight/downplay across all series
    let lastIdx = -1;
    const SERIES_COUNT = 1 + stackKeys.length;
    chart.on('updateAxisPointer', (event: any) => {
      const xa = event.axesInfo?.[0];
      const idx = (xa && xa.value !== undefined && xa.value >= 0) ? xa.value : -1;
      if (idx === lastIdx) return;
      if (lastIdx >= 0) {
        for (let s = 0; s < SERIES_COUNT; s++) {
          chart.dispatchAction({ type: 'downplay', seriesIndex: s, dataIndex: lastIdx });
        }
      }
      if (idx >= 0 && idx < values.length) {
        for (let s = 0; s < SERIES_COUNT; s++) {
          chart.dispatchAction({ type: 'highlight', seriesIndex: s, dataIndex: idx });
        }
      }
      lastIdx = idx;
    });
  }, [data, kPerBin, residueMap, filterHash, stackKeys, cm.categoryLabelsFromData]);

  return <div ref={containerRef} style={{ width: '100%', height: 220 }} />;
};
